<template>
  <div class="MyMapView" :class="{ 'MyMapView--commune': isCommunalModeActive }">
    <InfrastructureFicheModal
      v-if="myMapStore.infrastructureFicheModalId != null"
      :id-infrastructure="myMapStore.infrastructureFicheModalId"
      :attribute-title="myMapStore.infrastructureFicheModalAttributeTitle"
      :competence-id="myMapStore.infrastructureFicheModalCompetenceId"
      :feature-properties="myMapStore.infrastructureFicheModalFeatureProperties"
      @close="myMapStore.closeInfrastructureFicheModal()"
    />
    <MyMapHeader class="MyMapView__header" />
    <div class="MyMapView__mapCtn">
      <MyMapLeftSideBar class="MyMapView__welcomeSideBar" :shown="String(myMapStore.isLeftSidebarShown)" />
      <MyMap class="MyMapView__myMap" />
      <MyMapRightSideBar
        v-if="showRightSidebarLayers"
        class="MyMapView__layersSideBar"
        :shown="String(myMapStore.isRightSidebarShown)"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { LayerType } from '@/models/enums/geo/LayerType'
import { ItemType } from '@/models/enums/app/ItemType'
import { AppLayersService } from '@/services/map/AppLayersService'
import MapService from '@/services/map/MapService'
import { useMyMapStore } from '@/stores/myMapStore'
import InfrastructureFicheModal from '@/views/map/components/InfrastructureFicheModal.vue'
import MyMap from '@/views/map/components/MyMap.vue'
import MyMapHeader from '@/views/map/components/MyMapHeader.vue'
import MyMapLeftSideBar from '@/views/map/components/MyMapLeftSideBar.vue'
import MyMapRightSideBar from '@/views/map/components/MyMapRightSideBar.vue'
import { getGeoDataByCommuneQuery } from '@/data/communesGeoportail'
import { getGeoDataFromGeoJSON } from '@/data/communesCameroonGeoJSON'
import { CommuneService, mapApiResponseToCommuneData, type CommuneApiResponse } from '@/services/commune/CommuneService'
import {
  GEOPORTAIL_MODE_QUERY_KEY,
  GEOPORTAIL_COMMUNE_MODE_QUERY as GEOPORTAIL_MODE_COMMUNAL_VALUE,
} from '@/services/commune/navigateGeoportailCommune'
import { useProjectStore } from '@/stores/projectStore'
import { useResourceStore } from '@/stores/resourceStore'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

const GEOPORTAIL_OPEN_QUERY_KEY = 'open'
const GEOPORTAIL_OPEN_PROJECTS_VALUE = 'projects'
const GEOPORTAIL_RESET_PROJECT_FILTERS_QUERY_KEY = 'resetProjectFilters'
const BODY_CLASS_GEOPORTAIL_COMMUNE = 'body--geoportail-commune'

function getSingleQueryValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    const first = value.find((item) => typeof item === 'string')
    return typeof first === 'string' ? first : ''
  }
  return ''
}

function normalizeCommuneTitle(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

async function resolveCommuneFromQuery(communeQuery: string): Promise<CommuneApiResponse | null> {
  const query = communeQuery.trim()
  if (!query) return null
  const results = await CommuneService.searchCommunes(query, 20)
  if (!results.length) return null
  const normalizedQuery = normalizeCommuneTitle(query)
  return (
    results.find((item) => normalizeCommuneTitle(item.cmnTitre ?? '') === normalizedQuery) ??
    results[0]
  )
}

/** Set to true to show the right sidebar (layer list / myMap.rightSideBar). */
const showRightSidebarLayers = true

const myMapStore = useMyMapStore()
const projectStore = useProjectStore()
const resourceStore = useResourceStore()
const map = computed(() => myMapStore.myMap?.map)
const route = useRoute()
const isCommunalModeActive = computed(() => isCommuneGeoportailEntryFromHome())

async function applyCommuneFromQuery() {
  const communeQuery = route.query.commune
  if (typeof communeQuery !== 'string' || !communeQuery.trim()) {
    myMapStore.mapSearch = null
    return
  }

  // If mapSearch is already set to this commune (e.g. set by CommuneSearch), preserve it.
  // This prevents clearing a mapSearch that was just set by the UI, which would cause a
  // race condition where reenableMapInteraction's setTimeout locks the map permanently.
  const existingName = myMapStore.mapSearch?.name
  if (
    existingName &&
    existingName.trim().toLowerCase() === communeQuery.trim().toLowerCase()
  ) {
    return
  }

  // Priority 1: legacy static data (SOA and manually added communes).
  const staticGeoData = getGeoDataByCommuneQuery(communeQuery)
  if (staticGeoData) {
    myMapStore.mapSearch = staticGeoData
    return
  }

  // Priority 2: full GeoJSON dataset (365 communes with cmn_titre / cmn_code).
  const geoJsonData = await getGeoDataFromGeoJSON(communeQuery)
  if (geoJsonData) {
    myMapStore.mapSearch = geoJsonData
    return
  }

  myMapStore.mapSearch = null
}

/** Deep-link from project sheet: `/geoportail?project={uuid}` opens the project popup on the map. */
function shouldResetProjectFiltersFromQuery(): boolean {
  const raw = getSingleQueryValue(route.query[GEOPORTAIL_RESET_PROJECT_FILTERS_QUERY_KEY])
    .trim()
    .toLowerCase()
  return raw === '1' || raw === 'true' || raw === 'yes'
}

async function ensureProjectLayerDataReady(): Promise<void> {
  const mapComponent = myMapStore.myMap
  const rawMap = mapComponent?.map
  if (!mapComponent || !rawMap) return

  const sourceExists = rawMap.getSource(ItemType.PROJECT) != null
  if (!sourceExists) {
    await AppLayersService.setPlatformDataLayer(ItemType.PROJECT)
  }

  mapComponent.setData(
    ItemType.PROJECT,
    AppLayersService.getGeojsonForMapDisplay(ItemType.PROJECT),
  )
}

async function ensureResourceLayerDataReady(): Promise<void> {
  const mapComponent = myMapStore.myMap
  const rawMap = mapComponent?.map
  if (!mapComponent || !rawMap) return

  const sourceExists = rawMap.getSource(ItemType.RESOURCE) != null
  if (!sourceExists) {
    await AppLayersService.setPlatformDataLayer(ItemType.RESOURCE)
  }

  mapComponent.setData(
    ItemType.RESOURCE,
    AppLayersService.getGeojsonForMapDisplay(ItemType.RESOURCE),
  )
}

/** Deep-link from project sheet: `/geoportail?project={uuid}` opens the project popup on the map. */
async function applyProjectFromQuery(): Promise<void> {
  if (shouldResetProjectFiltersFromQuery()) {
    myMapStore.clearProjectMapFilters()
  }

  const open = getSingleQueryValue(route.query[GEOPORTAIL_OPEN_QUERY_KEY]).trim().toLowerCase()
  const raw = route.query.project
  const projectId = typeof raw === 'string' ? raw.trim() : ''
  const shouldOpenProjects = open === GEOPORTAIL_OPEN_PROJECTS_VALUE || projectId !== ''

  if (!shouldOpenProjects) return

  const projectMainLayer = myMapStore.projectLayer
  if (projectMainLayer != null) {
    projectMainLayer.isShown = true
  }
  await ensureProjectLayerDataReady()

  if (projectId === '') return
  myMapStore.activeItemId = projectId
}

function isCommuneGeoportailEntryFromHome(): boolean {
  const mode = getSingleQueryValue(route.query[GEOPORTAIL_MODE_QUERY_KEY]).trim().toLowerCase()
  return mode === GEOPORTAIL_MODE_COMMUNAL_VALUE
}

function applyCommuneLockFromEntry(): void {
  const communeQuery = getSingleQueryValue(route.query.commune).trim()
  if (isCommuneGeoportailEntryFromHome() && communeQuery) {
    myMapStore.setLockedCommuneName(communeQuery)
    return
  }
  myMapStore.setLockedCommuneName(null)
}

async function applyDefaultCommuneFondStyleFromQuery(): Promise<void> {
  if (!isCommuneGeoportailEntryFromHome()) return

  const communeQuery = getSingleQueryValue(route.query.commune).trim()
  if (!communeQuery) return

  if (myMapStore.visibleFondStyleIds.length > 0) return

  await myMapStore.fetchFondLayerStyles()
  const communeOnlyStyles = myMapStore.fondLayerStyles.filter(
    (style) => style.metadata?.visible_in === 'commune_only',
  )
  if (communeOnlyStyles.length === 0) return

  const explicitDefault = communeOnlyStyles.find(
    (style) => style.metadata?.default === true || style.metadata?.is_default === true,
  )

  const fallbackCommuneStyle = communeOnlyStyles.find((style) => style.id === 'commune')
  const styleToEnable = explicitDefault ?? fallbackCommuneStyle ?? communeOnlyStyles[0]

  if (!myMapStore.visibleFondStyleIds.includes(styleToEnable.id)) {
    myMapStore.setFondLayerVisible(styleToEnable.id, true)
  }
}

async function applyCommuneFicheFromQuery(): Promise<void> {
  const communeQuery = getSingleQueryValue(route.query.commune).trim()
  if (!communeQuery) {
    myMapStore.setCommuneFicheFromCommuneLayer(null)
    return
  }

  const existingFicheTitle =
    myMapStore.communeFicheForMapProfile?.cmn_titre ??
    myMapStore.communeFicheForMapProfile?.nom ??
    ''
  if (normalizeCommuneTitle(existingFicheTitle) === normalizeCommuneTitle(communeQuery)) {
    return
  }

  const commune = await resolveCommuneFromQuery(communeQuery)
  if (!commune) return

  myMapStore.setCommuneFicheFromCommuneLayer(mapApiResponseToCommuneData(commune))
  myMapStore.applyCommuneViewFromApiGeometry(commune)
  if (commune.cmnCode) {
    myMapStore.selectCommuneFeatureByCode(commune.cmnCode)
  }
}

onMounted(() => {
  applyCommuneLockFromEntry()
  void applyCommuneFromQuery()
  void applyCommuneFicheFromQuery()
  void applyDefaultCommuneFondStyleFromQuery()
  if (route.query.mapState) {
    myMapStore.serializedMapState = route.query.mapState as string
    myMapStore.deserializeMapState()
    void myMapStore.initMapLayers().then(() => applyProjectFromQuery())
    return
  }
  if (myMapStore.isMapAlreadyBeenMounted) {
    void AppLayersService.initApplicationLayers().then(() => applyProjectFromQuery())
    myMapStore.isLayersReorderingAlreadyTriggering = false
    MapService.isLoaded(map.value, () => reloadAtlasMaps())
  } else {
    void myMapStore.initMapLayers().then(() => applyProjectFromQuery())
  }
})

function syncBodyCommuneClass() {
  if (isCommunalModeActive.value) {
    document.body.classList.add(BODY_CLASS_GEOPORTAIL_COMMUNE)
  } else {
    document.body.classList.remove(BODY_CLASS_GEOPORTAIL_COMMUNE)
  }
}

watch(
  () => route.query[GEOPORTAIL_MODE_QUERY_KEY],
  () => syncBodyCommuneClass(),
  { immediate: true },
)

onUnmounted(() => {
  myMapStore.setLockedCommuneName(null)
  document.body.classList.remove(BODY_CLASS_GEOPORTAIL_COMMUNE)
})

watch(
  () => route.query.commune,
  () => {
    applyCommuneLockFromEntry()
    void applyCommuneFromQuery()
    void applyCommuneFicheFromQuery()
  },
)

watch(
  () => [route.query.project, route.query[GEOPORTAIL_OPEN_QUERY_KEY], route.query[GEOPORTAIL_RESET_PROJECT_FILTERS_QUERY_KEY], myMapStore.projectLayer] as const,
  () => {
    void applyProjectFromQuery()
  },
)

/**
 * Re-scope the projects and resources layers when entering/leaving the communal geoportail or
 * switching commune (GET /api/projects?cmnCode=..., GET /api/resources?cmnCode=...), same
 * principle as the infra/atlas_categories re-scoping.
 */
watch(
  () => myMapStore.activeCommuneCode,
  async () => {
    await Promise.all([
      projectStore.getAll(myMapStore.activeCommuneCode),
      resourceStore.getAll(myMapStore.activeCommuneCode),
    ])
    if (myMapStore.isProjectMapFiltersActive) {
      await myMapStore.applyProjectMapFilters()
    }
    if (myMapStore.isResourceMapFiltersActive) {
      await myMapStore.applyResourceMapFilters()
    }
    await Promise.all([ensureProjectLayerDataReady(), ensureResourceLayerDataReady()])
  },
)

function reloadAtlasMaps() {
  for (const item of myMapStore.legendList) {
    if (item.layerType === LayerType.ATLAS_LAYER) {
      myMapStore.updateAtlasLayersVisibility(item.id, false)
    }
  }
  myMapStore.setMapLayersOrderOnMapReMount()
}
</script>

<style lang="scss">
.MyMapView {
  display: flex;
  flex-flow: column nowrap;
  height: 100vh;
  overflow: hidden;
  .MyMapView__header {
    height: var(--mymap-header-h);
  }
  .MyMapView__mapCtn {
    display: flex;
    flex-flow: row nowrap;
    flex: 1 0 auto;

    .MyMapView__welcomeSideBar,
    .MyMapView__layersSideBar {
      z-index: 1;
      transition: all 0.15s ease-in;

      &[shown='false'] {
        overflow-x: hidden;
        width: 0 !important;
        min-width: 0 !important;
        flex: 0 0 0 !important;
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
}
</style>
